import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { buildHttpParams } from '../../../core/utils/http-util';
import { environment } from '../../../../environments/environment';
import { endpoints } from '../../core/constants/endpoints';
import {
  IKnowledgeDeleteAccepted,
  IKnowledgeDeleteAcceptedRaw,
  IKnowledgeDocument,
  IKnowledgeDocumentList,
  IKnowledgeDocumentListRaw,
  IKnowledgeDocumentRaw,
  IUpdateKnowledgeDocumentRequest,
  KnowledgeOcrMode,
  toKnowledgeDeleteAccepted,
  toKnowledgeDocument,
  toKnowledgeDocumentList,
  toUpdateKnowledgeDocumentRequestRaw
} from '../interfaces/knowledge-document.interface';

export interface IKnowledgeUploadRequest {
  file: File;
  title?: string;
  ocrMode?: KnowledgeOcrMode;
  idempotencyKey: string;
}

export interface IKnowledgeListFilter {
  limit?: number;
  cursor?: string;
}

@Injectable({ providedIn: 'root' })
export class KnowledgeApi {
  private readonly baseUrl = `${environment.ceppemAiApi}/${endpoints.ceppemAi.knowledge}`;

  constructor(private http: HttpClient) {}

  upload(request: IKnowledgeUploadRequest): Observable<IKnowledgeDocument> {
    const form = new FormData();
    form.append('file', request.file, request.file.name);

    if (request.title) {
      form.append('title', request.title);
    }
    form.append('ocr_mode', request.ocrMode ?? 'auto');

    const headers = new HttpHeaders({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Idempotency-Key': request.idempotencyKey
    });

    return this.http
      .post<IKnowledgeDocumentRaw>(`${this.baseUrl}/documents`, form, { headers })
      .pipe(map(toKnowledgeDocument));
  }

  list(filter?: IKnowledgeListFilter): Observable<IKnowledgeDocumentList> {
    const params = buildHttpParams({
      limit: filter?.limit,
      cursor: filter?.cursor
    });

    return this.http
      .get<IKnowledgeDocumentListRaw>(`${this.baseUrl}/documents`, { params })
      .pipe(map(toKnowledgeDocumentList));
  }

  getById(documentId: string): Observable<IKnowledgeDocument> {
    return this.http
      .get<IKnowledgeDocumentRaw>(`${this.baseUrl}/documents/${encodeURIComponent(documentId)}`)
      .pipe(map(toKnowledgeDocument));
  }

  update(
    documentId: string,
    request: IUpdateKnowledgeDocumentRequest
  ): Observable<IKnowledgeDocument> {
    return this.http
      .patch<IKnowledgeDocumentRaw>(
        `${this.baseUrl}/documents/${encodeURIComponent(documentId)}`,
        toUpdateKnowledgeDocumentRequestRaw(request)
      )
      .pipe(map(toKnowledgeDocument));
  }

  reprocess(
    documentId: string,
    ocrMode: KnowledgeOcrMode = 'auto'
  ): Observable<IKnowledgeDocument> {
    return this.http
      .post<IKnowledgeDocumentRaw>(
        `${this.baseUrl}/documents/${encodeURIComponent(documentId)}/ingestions`,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        { ocr_mode: ocrMode }
      )
      .pipe(map(toKnowledgeDocument));
  }

  downloadSource(documentId: string): Observable<Blob> {
    return this.http.get(
      `${this.baseUrl}/documents/${encodeURIComponent(documentId)}/source`,
      { responseType: 'blob' }
    );
  }

  delete(documentId: string): Observable<IKnowledgeDeleteAccepted> {
    return this.http
      .delete<IKnowledgeDeleteAcceptedRaw>(
        `${this.baseUrl}/documents/${encodeURIComponent(documentId)}`
      )
      .pipe(map(toKnowledgeDeleteAccepted));
  }
}
